// Wave 1 (2026-10) — frontend data cache pilot for the staff PM portal (Projects, Project
// Detail, Clients, Contacts, light pickers). ONE caching system: the TanStack QueryClient
// already mounted in index.js. Memory-only by design — no persister, no localStorage/
// sessionStorage of any cached response (see index.js: plain `new QueryClient()`, nothing
// added here changes that). Every query key is prefixed ["portal", userId, ...] so a 403/401
// mid-session can never resolve to a DIFFERENT user's cached data — see usePortalInvalidate's
// logout/user-switch handling in AuthContext.js and the three portal shells.
import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { api } from "@/api/client";

const LIST_STALE_MS = 60_000; // projects/clients/contacts lists — Notion-synced, not real-time
const DETAIL_STALE_MS = 30_000; // project/client detail — edited more often than a list segment
const REFERENCE_STALE_MS = 15 * 60_000; // roles/locations/field options/pickers — rarely change

function usePortalUserId() {
  const { user } = useAuth();
  return user?.id;
}

// Code-review fix (2026-10, Cursor audit P2) — a 401/403/404 means access was lost or the
// record is gone, not a transient blip. Every hook below routes through this so: (a) it never
// burns ~7s on react-query's default exponential-backoff retries before the error shows, and
// (b) the stale cached payload is dropped (once) instead of sitting behind a misleading
// "showing a saved copy, Retry" banner — only network/5xx errors still get that treatment.
function isPermissionError(error) {
  const status = error?.response?.status;
  return status === 401 || status === 403 || status === 404;
}

function useCachedQuery(queryKey, queryFn, { staleTime, enabled = true } = {}) {
  const qc = useQueryClient();
  const keyString = JSON.stringify(queryKey);
  const clearedForRef = useRef(null);
  const query = useQuery({
    queryKey,
    queryFn,
    staleTime,
    enabled,
    retry: (failureCount, error) => (isPermissionError(error) ? false : failureCount < 2),
  });
  useEffect(() => {
    if (query.isError && isPermissionError(query.error) && clearedForRef.current !== keyString) {
      clearedForRef.current = keyString; // once per distinct key — removeQueries below causes
      // exactly one more fetch attempt (still enabled); without this guard that attempt's
      // inevitable repeat 401/403/404 would re-trigger this same effect forever.
      qc.removeQueries({ queryKey, exact: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isError, query.error, keyString]);
  useEffect(() => { clearedForRef.current = null; }, [keyString]);
  // Cursor re-review PR #22, item 2 (regression): `removeQueries()` above does NOT notify
  // already-mounted observers in TanStack Query v5 — on an already-cached page, a BACKGROUND
  // refetch that comes back 401/403/404 left `query.data` holding the stale cached payload
  // (removeQueries only affects the cache store, not this render's own query result object),
  // so the "Showing a saved copy" banner and any still-live Edit actions never cleared. Every
  // consumer must see `data: undefined` the moment the error is a permission/not-found error,
  // not just after the next remount — so strip it here directly instead of relying solely on
  // removeQueries' side effect.
  if (query.isError && isPermissionError(query.error)) {
    return { ...query, data: undefined };
  }
  return query;
}

export function useProjectsBoardQuery(enabled = true) {
  const uid = usePortalUserId();
  return useCachedQuery(["portal", uid, "projects", "board"], () => api.get("/v1/projects/board").then((r) => r.data), { staleTime: LIST_STALE_MS, enabled: !!uid && enabled });
}

export function useProjectsAllQuery(includeArchived, enabled = true) {
  const uid = usePortalUserId();
  return useCachedQuery(["portal", uid, "projects", "all", includeArchived], () => api.get(`/v1/projects/?include_archived=${includeArchived}`).then((r) => r.data), { staleTime: LIST_STALE_MS, enabled: !!uid && enabled });
}

export function useProjectsPmMissingQuery(canAssignPm, enabled = true) {
  const uid = usePortalUserId();
  return useCachedQuery(["portal", uid, "projects", "pm-missing", canAssignPm], () => Promise.all([
    api.get("/v1/projects/pm-missing"),
    canAssignPm ? api.get("/v1/projects/pm-eligible-users") : Promise.resolve({ data: { items: [] } }),
  ]).then(([r, u]) => ({ items: r.data.items || [], total_count: r.data.total_count, eligible: u.data.items || [] })), { staleTime: LIST_STALE_MS, enabled: !!uid && enabled });
}

export function useProjectStatusesQuery() {
  const uid = usePortalUserId();
  return useCachedQuery(["portal", uid, "projects", "statuses"], () => api.get("/v1/projects/statuses").then((r) => r.data.statuses || []), { staleTime: REFERENCE_STALE_MS, enabled: !!uid });
}

export function useProjectDetailQuery(id, enabled = true) {
  const uid = usePortalUserId();
  return useCachedQuery(["portal", uid, "projects", id], () => api.get(`/v1/projects/${id}`).then((r) => r.data), { staleTime: DETAIL_STALE_MS, enabled: !!uid && !!id && enabled });
}

export function useClientsListQuery(segment, page, search, pageSize) {
  const uid = usePortalUserId();
  return useCachedQuery(["portal", uid, "clients", "list", segment, page, search], () => api.get(`/v1/clients/?segment=${segment}&page=${page}&page_size=${pageSize}&search=${encodeURIComponent(search)}`).then((r) => r.data), { staleTime: LIST_STALE_MS, enabled: !!uid });
}

export function useClientDetailQuery(id, enabled = true) {
  const uid = usePortalUserId();
  return useCachedQuery(["portal", uid, "clients", id], () => api.get(`/v1/clients/${id}`).then((r) => r.data), { staleTime: DETAIL_STALE_MS, enabled: !!uid && !!id && enabled });
}

export function useContactsListQuery(includeArchived) {
  const uid = usePortalUserId();
  return useCachedQuery(["portal", uid, "contacts", "list", includeArchived], () => api.get(`/v1/contacts/?include_archived=${includeArchived}`).then((r) => r.data.items || []), { staleTime: LIST_STALE_MS, enabled: !!uid });
}

export function useClientsPickerQuery(enabled = true) {
  const uid = usePortalUserId();
  return useCachedQuery(["portal", uid, "pickers", "clients"], () => api.get("/v1/clients/picker").then((r) => r.data.items || []), { staleTime: REFERENCE_STALE_MS, enabled: !!uid && enabled });
}

export function useProjectsPickerQuery(enabled = true) {
  const uid = usePortalUserId();
  return useCachedQuery(["portal", uid, "pickers", "projects"], () => api.get("/v1/projects/picker").then((r) => r.data.items || []), { staleTime: REFERENCE_STALE_MS, enabled: !!uid && enabled });
}

// Invalidation (Wave 1, point 5): mutations still call the same APIs as before — this just
// tells the cache which reads are now stale so the NEXT render/background refetch picks up
// the change, instead of every page doing its own ad-hoc refetch-everything.
export function usePortalInvalidate() {
  const qc = useQueryClient();
  const uid = usePortalUserId();
  return {
    project: (id) => {
      if (id) qc.invalidateQueries({ queryKey: ["portal", uid, "projects", id] });
      qc.invalidateQueries({ queryKey: ["portal", uid, "projects", "board"] });
      qc.invalidateQueries({ queryKey: ["portal", uid, "projects", "all"] });
      qc.invalidateQueries({ queryKey: ["portal", uid, "projects", "pm-missing"] });
      qc.invalidateQueries({ queryKey: ["portal", uid, "pickers", "projects"] });
    },
    projectLists: () => {
      qc.invalidateQueries({ queryKey: ["portal", uid, "projects", "board"] });
      qc.invalidateQueries({ queryKey: ["portal", uid, "projects", "all"] });
      qc.invalidateQueries({ queryKey: ["portal", uid, "projects", "pm-missing"] });
      qc.invalidateQueries({ queryKey: ["portal", uid, "pickers", "projects"] });
    },
    // Code-review fix (2026-10, Cursor audit P3-8) — a bulk delete must drop the deleted
    // projects' OWN detail cache entries, not just invalidate the lists; invalidate alone
    // would leave a deleted project's detail readable from cache until it happened to expire.
    removeProjectDetails: (ids) => { (ids || []).forEach((id) => qc.removeQueries({ queryKey: ["portal", uid, "projects", id], exact: true })); },
    client: (id) => {
      if (id) qc.invalidateQueries({ queryKey: ["portal", uid, "clients", id] });
      qc.invalidateQueries({ queryKey: ["portal", uid, "clients", "list"] });
      qc.invalidateQueries({ queryKey: ["portal", uid, "pickers", "clients"] });
    },
    clientLists: () => {
      qc.invalidateQueries({ queryKey: ["portal", uid, "clients", "list"] });
      qc.invalidateQueries({ queryKey: ["portal", uid, "pickers", "clients"] });
    },
    // Cursor re-review PR #22, item 4c — same reasoning as removeProjectDetails: a bulk/
    // single client delete must drop the deleted clients' OWN detail cache entries too, not
    // just invalidate the lists, or a stale cached copy stays readable until it expires.
    removeClientDetails: (ids) => { (ids || []).forEach((id) => qc.removeQueries({ queryKey: ["portal", uid, "clients", id], exact: true })); },
    // Cursor re-review PR #23 (Part 5), item 1 — a project only stores its client via a
    // Notion remote id (`project.client = [remote_id]`), never the client's own local id, so
    // there is no correct way to map a bulk-deleted project back to a specific affected
    // client id from the backend (the earlier `deleted_client_ids` attempt was dead code —
    // always `[]`). Bulk project delete instead invalidates the ENTIRE clients cache: the
    // bare ["portal", uid, "clients"] key matches the list query AND every individual client
    // detail entry (TanStack invalidateQueries matches any key starting with the given one),
    // so any client whose projects list changed gets refetched on next view.
    clientsAll: () => {
      qc.invalidateQueries({ queryKey: ["portal", uid, "clients"] });
      qc.invalidateQueries({ queryKey: ["portal", uid, "pickers", "clients"] });
    },
    contacts: () => qc.invalidateQueries({ queryKey: ["portal", uid, "contacts", "list"] }),
  };
}

// Prefetch on hover/focus (Wave 1, point 8) — debounced ~150ms, only for the specific
// visible row/card the pointer is over, never a whole list. Spread the returned handlers
// directly onto the card: `<div {...prefetchProps(p.id)}>`.
export function usePrefetchDetailOnHover(kind) {
  const qc = useQueryClient();
  const uid = usePortalUserId();
  const timers = useRef({});
  return (id) => {
    if (!id || !uid) return {};
    const fire = () => {
      const queryKey = ["portal", uid, kind === "project" ? "projects" : "clients", id];
      const url = kind === "project" ? `/v1/projects/${id}` : `/v1/clients/${id}`;
      qc.prefetchQuery({ queryKey, queryFn: () => api.get(url).then((r) => r.data), staleTime: DETAIL_STALE_MS });
    };
    return {
      onMouseEnter: () => { clearTimeout(timers.current[id]); timers.current[id] = setTimeout(fire, 150); },
      onFocus: () => { clearTimeout(timers.current[id]); timers.current[id] = setTimeout(fire, 150); },
      onMouseLeave: () => clearTimeout(timers.current[id]),
    };
  };
}
